import type { DonationAlertEvent } from '@shared/types'

import { OVERLAY_CONFIG } from '@shared/config'
import { useEffectSound } from '@shared/lib/audio'
import { type EffectComponentProps } from '@shared/lib/effect-registry'
import { chainAnimations, prefersReducedMotion } from '@shared/lib/waapi'
import { OverlayEffectSlot } from '@shared/ui/OverlayEffectSlot'
import { useEffect, useRef } from 'react'

import styles from './DonationAlert.module.css'

// Карточка алерта имеет свою фиксированную ширину (min/max-width в .card) —
// дефолтные width:35%/height:35% у зоны bottom-right здесь не нужны.
const SLOT_STYLE_OVERRIDE = { width: 'auto', height: 'auto' } as const

export function DonationAlert({ event, onDone }: EffectComponentProps<DonationAlertEvent>) {
  const rootRef   = useRef<HTMLDivElement>(null)
  const onDoneRef = useRef(onDone)
  onDoneRef.current = onDone

  useEffectSound(event)

  const duration = OVERLAY_CONFIG.DONATION_ALERT_DEFAULT_DURATION_MS
  const { from, amount, currency, message } = event.payload

  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    if (prefersReducedMotion()) {
      el.style.opacity='1'; el.style.transform='translateX(0)'
      const t=setTimeout(()=>onDoneRef.current(),duration); return ()=>clearTimeout(t)
    }
    const hold = Math.max(0, duration - 1100)
    void chainAnimations([
      () => el.animate([{transform:'translateX(120%)',opacity:0},{transform:'translateX(0)',opacity:1}],{duration:550,easing:'cubic-bezier(0.16,1,0.3,1)',fill:'forwards'}),
      () => el.animate([],{duration:hold,fill:'forwards'}),
      () => el.animate([{transform:'translateX(0)',opacity:1},{transform:'translateX(120%)',opacity:0}],{duration:500,easing:'cubic-bezier(0.7,0,0.84,0)',fill:'forwards'}),
    ]).then(()=>onDoneRef.current())
    return () => el.getAnimations().forEach((a) => a.cancel())
  }, [duration])

  return (
    <OverlayEffectSlot zone="bottom-right" animateIn={false} style={SLOT_STYLE_OVERRIDE}>
      <div ref={rootRef} className={styles.card} role="status" aria-live="polite">
        <div className={styles.header}>
          <span className={styles.avatar}>{from.charAt(0).toUpperCase()}</span>
          <span className={styles.from}>{from}</span>
        </div>
        <div className={styles.amount}>{currency} {amount.toLocaleString()}</div>
        {message && <p className={styles.message}>{message}</p>}
      </div>
    </OverlayEffectSlot>
  )
}
